import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from 'Components/Atoms/Box';
import TextField from 'Components/Atoms/TextField';
import Typography from 'Components/Atoms/Typography';
import Modal from 'Components/Molecules/Modal';
import { Claim } from 'types/noShowProtection';
import { refundClaim } from 'App/NoShowProtection/api';
import {
  apiErrorMessage,
  centsToEuroInput,
  formatCents,
  newRequestId,
  parseEuroToCents,
} from 'App/NoShowProtection/domain';

const NS = 'noShowProtection';

export const refundableCents = (claim: Claim) =>
  Math.max(0, claim.paidCents - claim.refundedCents - claim.chargedBackCents);

type RefundDialogProps = {
  restaurantId: string;
  claim: Claim;
  onClose: () => void;
  onRefunded: () => void;
};

/** Mounted per opening, so the requestId stays stable across retries of one dialog. */
const RefundDialog: React.FC<RefundDialogProps> = ({
  restaurantId,
  claim,
  onClose,
  onRefunded,
}) => {
  const { t } = useTranslation(NS);
  const [requestId] = useState(newRequestId);
  const maxCents = refundableCents(claim);
  const [amount, setAmount] = useState(centsToEuroInput(maxCents));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    const amountCents = parseEuroToCents(amount);
    if (!Number.isFinite(amountCents) || amountCents < 1) {
      setError(t('Please enter an amount.'));
      return;
    }
    if (amountCents > maxCents) {
      setError(
        t('At most {{amount}} can be refunded.', { amount: formatCents(maxCents) })
      );
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await refundClaim(restaurantId, claim.id, requestId, amountCents);
      onRefunded();
      onClose();
    } catch (e) {
      setError(apiErrorMessage(e, t('The refund failed.')));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={t('Refund fee')}
      maxWidth="xs"
      dontCloseAfterSubmit
      actions={[
        { id: 'cancel', label: t('Cancel'), translation: null, onClick: onClose },
        {
          id: 'submit',
          label: t('Refund'),
          translation: null,
          variant: 'primary',
          loading: submitting,
          disabled: submitting,
          onClick: submit,
        },
      ]}
    >
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Typography variant="text-3" color="subdued" translation={null} block>
          {t('Refundable: {{amount}}', { amount: formatCents(maxCents) })}
        </Typography>
        <TextField
          value={amount}
          label="Amount (€)"
          labelTranslation={NS}
          inputProps={{ inputMode: 'decimal' }}
          onChange={(e) => setAmount(e.target.value)}
        />
        {error && (
          <Typography variant="text-3" color="error" translation={null} block>
            {error}
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default RefundDialog;
